import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {Universe} from '../../../../../models/universe';
import {ApiUniverseService} from '../../../../../services/sp-api/sp-api-universe/api-universe.service';
import {
  AudienceType,
  ProjectCreationManagerService
} from '../../../../../services/sp-project/project-creation-manager.service';
import {Audience} from '../../../../../models/audience';
import {RoleUtils} from '../../../../../utils/role/role-utils';
import {
  ApiAudiencePresetService
} from '../../../../../services/sp-api/sp-api-audience-preset/api-audience-preset.service';
import {Targeting} from '../../../../../classes/targeting/targeting';
import {toFbJsonString} from '../../../../../classes/targeting/meta-targeting';
import {CountryJson} from '../../../../../services/sp-api/api.service';
import {environment} from '../../../../../../environments/environment';
import {LoaderService} from '../../../../../services/sp-loading/loader.service';
import {
  ApiAudienceCategoryService,
  ReportData
} from '../../../../../services/sp-api/sp-api-audience-category/api-audience-category.service';
import {TreeComponent} from '../../../../../user-interface/tree-component/tree-component';
import {TreeSearchService} from '../../../../../services/tree-search/tree-search.service';
import {Subscription} from 'rxjs';
import {AlertService} from '../../../../../services/sp-alert/alert.service';
import {AudiencePreset} from '../../../../../models/audience-preset';
import { SessionProviderService } from 'src/app/services/sp-authentication/session-provider.service';

export interface ProjectSetupOptions {
  universeNameFallback?: string;
  showPresetSelection?: boolean;
}

interface ProjectChangeEvent {
  name: string;
  valid: boolean;
}

@Component({
  selector: 'app-sp-project-create-project-setup',
  templateUrl: './sp-project-create-project-setup.component.html',
  styleUrls: ['./sp-project-create-project-setup.component.scss']
})
export class SpProjectCreateProjectSetupComponent implements OnInit, AfterViewInit , OnDestroy {

  constructor(
    private creationManager: ProjectCreationManagerService,
    private apiUniverse: ApiUniverseService,
    private sessionProvider: SessionProviderService,
    private apiAudiencePreset: ApiAudiencePresetService,
    private loader: LoaderService,
    protected apiAudienceCategoryService: ApiAudienceCategoryService,
    private treeSearchService: TreeSearchService,
    private alert: AlertService
  ) {
  }


  get currentAudience(): Audience { return this.creationManager.currentAudience; }
  get audienceTarget(): Audience { return this.creationManager.audienceTarget; }
  get audienceBenchmark(): Audience { return this.creationManager.audienceBenchmark; }
  get projectTitleLink(): boolean { return this.creationManager.projectTitleLink; }
  get flag(): string|undefined { return this.creationManager.flag; }

  get topicUniverseSelected(): Universe|undefined { return this.creationManager.topicUniverseSelected; }
  get politicalUniverseSelected(): Universe|undefined { return this.creationManager.politicalUniverseSelected; }
  get cicCountriesSelected(): CountryJson[]|undefined { return this.creationManager.cicCountriesSelected; }
  @Input() readonly options: ProjectSetupOptions;
  @Output() private readonly projectNameChange: EventEmitter<ProjectChangeEvent> = new EventEmitter();

  @ViewChild(TreeComponent) private treeComponent: TreeComponent;
  @ViewChild('projectNameInput') private projectNameInput: ElementRef<HTMLInputElement>;

  showAudienceLibrary = false;

  rootElements: { id: number, name: string }[] = [];
  readonly myReportAudiencesLeftNode: { id: number, name: string } = { id: -1, name: 'My Reports Audiences' };
  selectedLeftSideNode: { id: number, name: string };

  projectNameControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  selectedUniverse: Universe;
  selectedUniverses: Universe[] = [];
  universes: Universe[] = [];

  freemium = false;


  countryList: CountryJson[] = [];
  topicsList: Universe[] = [];
  politicsList: Universe[] = [];

  private subscriptions: Subscription[] = [];
  @ViewChild('audienceLibrary') spTestElement: ElementRef;

  selectedAudience: any = null;

  protected readonly top = top;
  protected readonly environment = environment;

  /*
   * Note on "Default" universe: it is automatically set up in API if not selected
   * Here the default universe refers to the default selection, and not the default universe included in every project,
   * we need to clarify that
   */

  async ngOnInit() {
    this.loader.load();
    try {
      this.rootElements = await this.apiAudienceCategoryService.getCategoriesRootElements();
      this.rootElements.push(this.myReportAudiencesLeftNode);
      if (this.rootElements.length > 0) {
        this.selectedLeftSideNode = this.rootElements[0];
      }
    } catch (error) {
      console.error('Error loading root elements:', error);
    }

    const response = await this.apiUniverse.getAll();
    const universes: Universe[] = Array.isArray(response) ? response : response.data;
    this.universes = universes.filter(u => !u.default);
    let universeDefault = this.universes.find(u => u.default_select);
    if (!universeDefault && this.options.universeNameFallback) {
      universeDefault = this.universes.find(u => u.name === this.options.universeNameFallback);
    }
    if (universeDefault) {
      this.selectedUniverse = universeDefault;
      this.onUniverseSelected(universeDefault);
    }

    const permCrawlCustom = await this.sessionProvider.activeSession.hasPermission('crawl.custom');
    if (permCrawlCustom) {
      this.apiUniverse.getCountryList()
          .then(countryList => this.countryList = countryList);

      this.apiUniverse.getTopicUniverses()
          .then(topics => this.topicsList = topics);

      this.apiUniverse.getPoliticalUniverses()
        .then(politics => this.politicsList = politics);
    }

    // Initialize projectNameControl with current project name value
    const currentProjectName = this.creationManager.projectName;
    if (currentProjectName) {
      this.projectNameControl.setValue(currentProjectName);
      // Also update audience names if projectTitleLink is enabled
      if (this.projectTitleLink) {
        this.audienceTarget.name = currentProjectName;
        this.audienceBenchmark.name = currentProjectName + ' - Benchmark';
      }
    }

    this.subscriptions.push(this.creationManager.projectNameUpdateSubject.subscribe(name => {
      this.projectNameControl.setValue(name);
      this.onProjectNameChange(name);
    }));

    this.subscriptions.push(this.creationManager.wrongProjectNameSubject.subscribe(() => {
      this.focusProjectName();
    }));

    if (this.sessionProvider.activeSession) {
      this.freemium = this.sessionProvider.activeSession.user.role.level <= RoleUtils.freemiumLevel;
    }

    this.loader.loaded();
  }

  ngAfterViewInit(){
    if (this.treeComponent && this.selectedLeftSideNode) {
      this.treeComponent.selectedLeftSideNode = this.selectedLeftSideNode;
    }
  }


  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.spTestElement && this.spTestElement.nativeElement) {
      if (!this.spTestElement.nativeElement.contains(event.target)) {
        this.showAudienceLibrary = false;
      }
    }
  }

  resetAudiencePreset(update: boolean = true) {
    this.projectNameControl.setValue('');
    this.creationManager.resetAudienceFields(this.creationManager.audienceType, false, true, true);
    this.selectedAudience = null;
    this.creationManager.clearAudienceSelectionTracking();

    if (this.treeComponent) {
      this.treeComponent.clearNodeSelection();
    }

    if (update) {
      this.creationManager.updateAudience(this.creationManager.audienceType).then();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Brings the user to the project name field. The launch button lives in a header that stays
   * pinned while this card scrolls out of view, so marking the control invalid is not enough
   * on its own -- the user never sees the highlight.
   */
  focusProjectName() {
    this.projectNameControl.markAllAsTouched();

    const input = this.projectNameInput ? this.projectNameInput.nativeElement : undefined;
    if (!input) { return; }

    input.scrollIntoView({behavior: 'smooth', block: 'center'});
    input.focus({preventScroll: true});
  }

  onProjectNameChange(name: string) {
    this.creationManager.changeProjectName(name, false);
    if (name !== '' && this.projectTitleLink) {
      this.audienceTarget.name = name;
      this.audienceBenchmark.name = name + ' - Benchmark';
    }

    const event: ProjectChangeEvent = {
      name,
      valid: this.projectNameControl.valid,
    };

    this.projectNameChange.emit(event);
  }

  /**
   * Event for when a universe is selected
   */
  onUniverseSelected(universe: Universe) {
    this.selectedUniverses = [];
    this.selectedUniverses.push(universe);
    this.creationManager.setProjectUniverses(this.selectedUniverses);
  }

  onTopicUniverseSelected(universe: Universe) {
    this.creationManager.topicUniverseSelected = universe;
  }

  onPoliticalUniverseSelected(universe: Universe) {
    this.creationManager.politicalUniverseSelected = universe;
  }

  onCicCountriesSelected(countries: CountryJson[]) {
    this.creationManager.cicCountriesSelected = countries;
  }

  isNodeExpandable(node: any): boolean {
    return node.type === 'category';
  }

  /**
   * Called when the search event is triggered from the TitanInput component
   * Stores the search query and refreshes the tree
   */
  async onTreeSearch(query: string): Promise<void> {
    // Update the shared search service
    this.treeSearchService.updateSearchText(query);

    // Track search query
    if (query && query.trim() !== '') {
      this.creationManager.trackAudienceSearch(query, this.selectedLeftSideNode?.name || '');
    }

    // Wait for the next tick to ensure the ViewChild reference is updated
    // if we've switched between categories and reports trees

      // The tree component will handle the search via its subscription
    if (this.treeComponent) {
        await this.treeComponent.refreshTree();
      }

  }

  /**
   * Called when a root element button is clicked
   */
  async onLeftSideNodeClick(node: { id: number, name: string }): Promise<void> {
    if (this.selectedLeftSideNode?.id === node.id) {
      return;
    }

    this.selectedLeftSideNode = node;

    // Set the selected node in the tree component
    if (this.treeComponent) {
      this.treeComponent.selectedLeftSideNode = this.selectedLeftSideNode;
      this.treeComponent.currentPage = 1; // Reset pagination
    }

    this.treeComponent.refreshTree();
  }

  isNodeTheSelectedLeftSideNode(node: { id: number, name: string }): boolean {
    return this.selectedLeftSideNode?.id === node.id;
  }

  searchAudienceDefinitions(page: number = 1, perPage: number = 100, searchText: string): Promise<{ id: number, name: string }[]> {
    // Using v2 search endpoint
    return this.apiAudienceCategoryService.searchCategoryChildren(this.selectedLeftSideNode.id, page, perPage, searchText);
  }

  /// Report Audiences section

  async getReportsForTree(page: number = 1, perPage: number = 100, searchParam?: string): Promise<{ id: number, name: string, created_at?: string }[]> {
    const response = await this.apiAudienceCategoryService.getReports(page, perPage, searchParam);
    return response.data;
  }

  isMyReportsAudiencesSelected(): boolean {
    return (
      this.selectedLeftSideNode &&
      this.selectedLeftSideNode.id === this.myReportAudiencesLeftNode.id
    );
  }

  /**
   * Unified method to get children for either reports or categories
   */
  getNodeChildren(nodeId: number | null, categoryPage: number = 1, categoryPerPage: number = 100, presetPage: number = 1, presetPerPage: number = 100): Promise<{ id: number, name: string, created_at?: string }[]> {
    // For root elements (null nodeId), use the selectedLeftSideNode
    if (nodeId === null) {
      if (!this.selectedLeftSideNode || this.selectedLeftSideNode.id === 0) {
        return Promise.resolve([]);
      }

      if (this.isMyReportsAudiencesSelected()) {
        // Default to page 1 and per_page 15 for initial data loading
        return this.getReportsForTree(categoryPage, categoryPerPage);
      } else {
        // Use provided page and per_page for initial load
        return this.apiAudienceCategoryService.getCategoryChildren(this.selectedLeftSideNode.id, categoryPage, categoryPerPage, presetPage, presetPerPage);
      }
    }

    // Regular child node loading
    if (this.isMyReportsAudiencesSelected()) {
      return this.getReportsForTree(categoryPage, categoryPerPage);
    } else {
      return this.apiAudienceCategoryService.getCategoryChildren(nodeId, categoryPage, categoryPerPage, presetPage, presetPerPage);
    }
  }

  /**
   * Unified method to search tree nodes based on the current left side selection
   */
  searchTreeNodes(page: number = 1, perPage: number = 100, searchText: string): Promise<{ id: number, name: string, created_at?: string }[]> {
    if (this.isMyReportsAudiencesSelected()) {
      return this.getReportsForTree(page, perPage, searchText);
    } else {
      return this.searchAudienceDefinitions(page, perPage, searchText);
    }
  }

  /**
   * Unified node selection handler - directs to the appropriate handler based on node type
   */
   async onNodeSelected(node: any): Promise<void> {
    this.showAudienceLibrary = false;
    this.selectedAudience = node;
    const selectedUniverses: Universe[] = [...this.creationManager.currentAudience.universes];
    let audiencePresetSpec: Targeting;

    if (this.isMyReportsAudiencesSelected()) {
       const reportData: ReportData = await this.apiAudienceCategoryService.getReport(node.id);
       audiencePresetSpec = Targeting.fromJson(reportData.audience_target.target_spec, 'sociodemo');

       if (this.creationManager.audienceType === AudienceType.target) {
        this.creationManager.changeProjectName(reportData.name);
       }
       this.creationManager.trackAudienceDefinitionSelection(
         reportData.name,
         reportData.id,
         this.selectedLeftSideNode.name,
         undefined
       );
    } else {
      const audiencePreset: AudiencePreset = await this.apiAudiencePreset.getOne(node.id);

      const audience: Audience = Audience.fromPreset(audiencePreset);
      audiencePresetSpec = Targeting.fromJson(audiencePreset.target_spec, 'sociodemo');

      // Check if the audience preset has geolocations, if not, use the current audience geolocations
      // We do not check the same condition for a report selection because the audience from the report has geolocations already
      if (!audiencePresetSpec.geolocations) {
        audiencePresetSpec.geolocations = this.creationManager.currentAudience.targeting.geolocations;
      }

      audience.target_spec = toFbJsonString(audiencePresetSpec);
      this.creationManager.pushAudienceType(audience, this.creationManager.audienceType);

      if (this.creationManager.audienceType === AudienceType.target) {
        this.creationManager.changeProjectName(audiencePreset.name);
      }

      this.creationManager.trackAudienceDefinitionSelection(
        audiencePreset.name,
        audiencePreset.id,
        this.selectedLeftSideNode.name,
        audiencePreset.category?.name
      );
    }

    const hasInterestExclusions: boolean = audiencePresetSpec.exclusions && audiencePresetSpec.exclusions.size > 0;

    if (hasInterestExclusions) {
      this.alert.notify(
        'Interests Exclusions Removed',
        'Your audience included interest exclusions. Exclusions are now deprecated and have been automatically removed from the targeting of your selected audience.',
        'warning'
      );

      audiencePresetSpec.removeExclusions();
    }

    this.creationManager.loadAudienceInfoFromTargeting(audiencePresetSpec, this.creationManager.audienceType);
    this.creationManager.setProjectUniverses(selectedUniverses);
    await this.creationManager.updateAudience(this.creationManager.audienceType);
  }
}
